import '@/shared/utils/prelude';
import 'mac-scrollbar/dist/mac-scrollbar.css';
import '../styles/globals.css';
import '@rainbow-me/rainbowkit/styles.css';
import '@solana/wallet-adapter-react-ui/styles.css';
import { type JSX } from 'react';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Script from 'next/script';
import * as Sentry from '@sentry/react';
import { Toaster } from 'sonner';
import FallbackLayout from '@/shared/components/organisms/FallbackLayout';
import { Provider as RainbowKitWrappedProvider } from '@/shared/components/RainbowKit';
import { SolanaProvider } from '@/shared/components/SolanaConnect';
import { TronProvider } from '@/shared/components/TronConnect';
import { isTestnet } from '@/shared/featureFlags';
import { useCanonicalUrl } from '@/shared/hooks/useCanonicalUrl';
import { ChainBalancesProvider } from '@/shared/hooks/useChainBalances';
import { CoinGeckoPricesProvider } from '@/shared/hooks/useCoinGeckoPrices';
import usePlausible from '@/shared/hooks/usePlausible';
import { getChainflipNetwork } from '@/shared/utils/env';
import { initSentry } from '@/shared/utils/helpers';
import { multiChainRainbowConfig } from '@/shared/utils/rainbowkit';
import Layout from '../components/Layout';
import { SWAPPING_APP_DOMAINS } from '../utils/consts';
import { getPageDescription, getPageTitle } from '../utils/seo';

initSentry();

function MyApp({ Component, pageProps }: AppProps): JSX.Element {
  usePlausible();

  const network = getChainflipNetwork();
  const domain = SWAPPING_APP_DOMAINS[network === 'unknown' ? 'perseverance' : network];
  const ogType = network === 'mainnet' ? 'mainnet' : 'testnet';
  const canonicalUrl = useCanonicalUrl();
  const { pathname } = useRouter();
  const description = getPageDescription(pathname);
  const title = getPageTitle(pathname);

  return (
    <>
      <Head>
        <link rel="icon" href="/chainflip-favicon.ico" sizes="any" />
        <link rel="icon" href="/chainflip-favicon.svg" type="image/svg+xml" />
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
        <title>{title}</title>
        <meta name="description" content={description} />

        <meta property="og:url" content={`https://${domain}`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={`https://${domain}/chainflip-${ogType}-og.png`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="twitter:domain" content={domain} />
        <meta property="twitter:url" content={`https://${domain}`} />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={`https://${domain}/chainflip-${ogType}-og.png`} />
        {isTestnet && <meta name="robots" content="noindex, nofollow" />}
      </Head>
      <Script
        id="next-plausible-init"
        dangerouslySetInnerHTML={{
          __html: `window.plausible = window.plausible || function() { (window.plausible.q = window.plausible.q || []).push(arguments) }`,
        }}
      />
      <Sentry.ErrorBoundary
        // eslint-disable-next-line react/no-unstable-nested-components
        fallback={({ error, resetError }) => {
          const errorMessage = (error as Error).message;
          return <FallbackLayout message={errorMessage} onClose={resetError} />;
        }}
      >
        <RainbowKitWrappedProvider config={multiChainRainbowConfig}>
          <SolanaProvider>
            <TronProvider>
              <CoinGeckoPricesProvider>
                <ChainBalancesProvider>
                  <Layout>
                    <Component {...pageProps} />
                    <Toaster
                      theme="dark"
                      richColors
                      toastOptions={{
                        style: {
                          fontSize: '12px',
                          fontFamily: 'Aeonik-Medium',
                        },
                      }}
                    />
                  </Layout>
                </ChainBalancesProvider>
              </CoinGeckoPricesProvider>
            </TronProvider>
          </SolanaProvider>
        </RainbowKitWrappedProvider>
      </Sentry.ErrorBoundary>
    </>
  );
}

export default MyApp;
